import React, { forwardRef, useRef, useEffect, useState, useCallback } from 'react';
import { Box, TextField, InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, MyLocation as MyLocationIcon } from '@mui/icons-material';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { UniversalMapProps, LatLng, UniversalMapEntry } from './MapInterface';

// Render function for Google Maps loading states
const render = (status: Status): React.ReactElement => {
  switch (status) {
    case Status.LOADING:
      return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>Loading Google Maps...</Box>;
    case Status.FAILURE:
      return <Box sx={{ p: 2, color: 'error.main' }}>Error loading Google Maps</Box>;
    default:
      return <Box>Ready</Box>;
  }
};

interface GoogleMapComponentProps extends UniversalMapProps {}

const GoogleMapComponentInner = forwardRef<HTMLDivElement, GoogleMapComponentProps>(({
  center,
  zoom,
  className,
  style,
  onMapClick,
  onLocationSearch,
  onMarkerClick,
  markers = [],
  entries = [],
  route,
  showSearch = true,
  showControls = true,
  interactive = true,
  ...props
}, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete>();
  const [searchValue, setSearchValue] = useState('');
  const [entryMarkers, setEntryMarkers] = useState<google.maps.Marker[]>([]);

  // Initialize Google Map
  useEffect(() => {
    if (mapRef.current && !map) {
      const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
      const newMap = new google.maps.Map(mapRef.current, {
        center: { lat: center.lat, lng: center.lng },
        zoom,
        clickableIcons: false,
        mapId,
        styles: [
          {
            featureType: 'administrative',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#c0c0c0' }, { weight: 1 }]
          },
          {
            featureType: 'water',
            stylers: [{ color: '#c6e2ff' }]
          },
          {
            featureType: 'road',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'transit',
            stylers: [{ visibility: 'off' }]
          }
        ],
        disableDefaultUI: !showControls,
        zoomControl: showControls,
        gestureHandling: interactive ? 'auto' : 'none',
      });

      // Add click listener if provided
      if (onMapClick) {
        newMap.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            onMapClick({
              lat: event.latLng.lat(),
              lng: event.latLng.lng(),
            });
          }
        });
      }

      setMap(newMap);
    }
  }, [mapRef.current, center, zoom, showControls, interactive, onMapClick]);

  // Update map center when prop changes
  useEffect(() => {
    if (map) {
      map.setCenter({ lat: center.lat, lng: center.lng });
    }
  }, [map, center]);

  // Update map zoom when prop changes
  useEffect(() => {
    if (map) {
      map.setZoom(zoom);
    }
  }, [map, zoom]);

  // Update map controls and interactivity when props change
  useEffect(() => {
    if (map) {
      map.setOptions({
        disableDefaultUI: !showControls,
        zoomControl: showControls,
        gestureHandling: interactive ? 'auto' : 'none',
      });
    }
  }, [map, showControls, interactive]);

  // Two-step search handler instead of expensive Google Places Autocomplete
  const handleSearchSubmit = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || !map) return;

    try {
      console.log('🔍 GoogleMap: Starting two-step search for:', searchQuery);
      
      // Use our two-step search API instead of expensive Google Places API
      const response = await fetch(`/api/searchPlaces?query=${encodeURIComponent(searchQuery)}&debug=true`);
      const data = await response.json();
      
      console.log('🔍 GoogleMap: Two-step search response:', data);
      
      if (data.success && data.results && data.results.length > 0) {
        const result = data.results[0];
        const location = {
          lat: result.lat,
          lng: result.lng
        };
        
        console.log('🔍 GoogleMap: Found location:', location, 'from provider:', result.provider);
        
        // Center map on selected location
        map.setCenter(location);
        map.setZoom(15); // Zoom in to show details
        
        if (onLocationSearch) {
          // Use the business name or formatted address
          const placeName = result.name || result.display_name || 'Unknown Location';
          onLocationSearch(location, placeName);
        }
        setSearchValue('');
      } else {
        console.warn('🔍 GoogleMap: No results found for search query:', searchQuery);
        console.warn('🔍 GoogleMap: Search response:', data);
      }
    } catch (error) {
      console.error('🔍 GoogleMap: Error in two-step search:', error);
    }
  }, [map, onLocationSearch]);

  // Initialize Google Places Autocomplete for search (DISABLED - using two-step search instead)
  useEffect(() => {
    // Temporarily disabled to use cost-effective two-step search
    // if (map && showSearch && searchInputRef.current && !autocomplete) {
    //   // Create autocomplete with enhanced options for better business search
    //   const newAutocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
    //     fields: ['name', 'formatted_address', 'geometry', 'place_id', 'types'],
    //     types: ['establishment', 'geocode'], // Include businesses and addresses
    //   });
    //   
    //   // Bias search results to current map viewport
    //   newAutocomplete.bindTo('bounds', map);
    //   
    //   // Handle place selection
    //   newAutocomplete.addListener('place_changed', () => {
    //     const place = newAutocomplete.getPlace();
    //     if (place.geometry?.location && onLocationSearch) {
    //       const location = {
    //         lat: place.geometry.location.lat(),
    //         lng: place.geometry.location.lng()
    //       };
    //       
    //       // Use business name if available, otherwise use formatted address
    //       const placeName = place.name || place.formatted_address || 'Selected Location';
    //       
    //       // Center map on selected location
    //       map.setCenter(location);
    //       map.setZoom(15); // Zoom in to show details
    //       
    //       // Trigger location search callback
    //       onLocationSearch(location, placeName);
    //       
    //       // Clear search input
    //       setSearchValue('');
    //     }
    //   });

    //   setAutocomplete(newAutocomplete);
    // }
  }, [map, showSearch, autocomplete, onLocationSearch]);

  // Update markers when props change
  useEffect(() => {
    if (map) {
      // Clear existing markers
      entryMarkers.forEach(marker => marker.setMap(null));
      const newMarkers: google.maps.Marker[] = [];

      // Add temporary markers (from map clicks or search)
      markers.forEach((markerInfo) => {
        const marker = new google.maps.Marker({
          position: { lat: markerInfo.position.lat, lng: markerInfo.position.lng },
          map,
          title: markerInfo.title,
          icon: {
            url: '/map_pin.png',
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 32), // Anchor at bottom center of pin
          }
        });
        newMarkers.push(marker);
      });

      // Add entry markers
      entries.forEach((entry) => {
        const marker = new google.maps.Marker({
          position: { lat: entry.location.lat, lng: entry.location.lng },
          map,
          title: entry.title,
          icon: {
            url: '/map_pin.png',
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 32), // Anchor at bottom center of pin
          }
        });

        // Add click handler for entry markers
        if (onMarkerClick) {
          marker.addListener('click', () => {
            onMarkerClick(entry);
          });
        }

        newMarkers.push(marker);
      });

      setEntryMarkers(newMarkers);
    }
  }, [map, markers, entries, onMarkerClick]);

  // Route rendering effect
  useEffect(() => {
    if (!map || !route || !route.geometry || route.geometry.length === 0) {
      return;
    }

    // Create DirectionsRenderer if it doesn't exist
    const directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true, // We'll use our own markers
      polylineOptions: {
        strokeColor: '#3b82f6',
        strokeWeight: 4,
        strokeOpacity: 0.8
      }
    });

    directionsRenderer.setMap(map);

    // Create a path from the route geometry
    const path = route.geometry.map(point => ({
      lat: point.lat,
      lng: point.lng
    }));

    // Create a Polyline for the route
    const routeLine = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#3b82f6',
      strokeOpacity: 0.8,
      strokeWeight: 4
    });

    routeLine.setMap(map);

    console.log('Route added to Google Maps:', route);

    // Cleanup function
    return () => {
      directionsRenderer.setMap(null);
      routeLine.setMap(null);
    };
  }, [map, route]);

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
  }, []);

  const handleGetCurrentLocation = useCallback(() => {
    if (navigator.geolocation && map) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          map.setCenter(location);
          map.setZoom(15);
          if (onLocationSearch) {
            onLocationSearch(location, 'Current Location');
          }
        },
        (error) => {
          console.error('Error getting current location:', error);
        }
      );
    }
  }, [map, onLocationSearch]);

  return (
    <Box 
      ref={ref}
      className={className}
      style={style}
      sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}
    >
      {showSearch && (
        <Box
          ref={searchContainerRef}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search for businesses, places, or addresses..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchSubmit(searchValue);
              }
            }}
            size="small"
            inputRef={searchInputRef}
            sx={{
              bgcolor: 'background.paper',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton 
                    size="small" 
                    onClick={() => handleSearchSubmit(searchValue)}
                    disabled={!searchValue.trim()}
                  >
                    <SearchIcon color="action" />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchValue && (
                    <IconButton size="small" onClick={handleSearchClear}>
                      <ClearIcon />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={handleGetCurrentLocation}>
                    <MyLocationIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
      
      <Box
        ref={mapRef}
        sx={{ width: '100%', height: '100%', minHeight: 400 }}
      />
    </Box>
  );
});

GoogleMapComponentInner.displayName = 'GoogleMapComponentInner';

// Main component wrapped with Google Maps API loader
const GoogleMapComponent = forwardRef<HTMLDivElement, GoogleMapComponentProps>((props, ref) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        Google Maps API key not configured
      </Box>
    );
  }

  return (
    <Wrapper
      apiKey={apiKey}
      render={render}
      libraries={['places', 'geometry']}
    >
      <GoogleMapComponentInner ref={ref} {...props} />
    </Wrapper>
  );
});

GoogleMapComponent.displayName = 'GoogleMapComponent';

export default GoogleMapComponent;